import { useState } from 'react'
import {
  Card,
  CardContent,
  Link,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Typography,
  Button
} from '@mui/material'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import LeadHeader from '../../../components/LeadHeader/LeadHeader'
import PageLoading from '../../../components/PageLoading/PageLoading'
import useLead from '../../../hooks/useLead'
import { capitalize } from '../../../utils/string'
import {
  compareRawDates,
  getDateFromSpanishDateTime,
  dateToDateTime,
  dateToStringLong
} from '../../../utils/date'
import {
  formatDuration,
  getEstadoRespuestaLlamada
} from '../../../utils/model/llamadas'
import ModalContactoLead from '../../../modals/ModalContactoLead/ModalContactoLead'
import ModalContactoPrueba from '../../../modals/ModalContactoPrueba/ModalContactoPrueba'
import ModalContactoLeadCualificado from '../../../modals/ModalContactoLeadCualificado/ModalContactoLeadCualificado'
import ModalContactoLeadCerrado from '../../../modals/ModalContactoLeadCerrado/ModalContactoLeadCerrado'
import ModalTranscripcionLlamada from '../../../modals/ModalTranscripcionLlamada/ModalTranscripcionLlamada'
import { addContacto, modificarPrueba } from '../../../utils/api/leads'
import { getLlamadasLead } from '../../../utils/api/llamadas'
import { useNotification } from '../../../context/NotificationManager/NotificationManager'
import ModalEditarPrueba from '../../../modals/ModalEditarPrueba/ModalEditarPrueba'
import { getAuth } from '../../../utils/auth'
import css from './Contactos.module.css'

const NUEVO_CONTACTO_OPTIONS = [
  { name: 'Cerrar', icon: <i className='material-icons'>handshake</i> },
  { name: 'Presupuesto', icon: <i className='material-icons'>verified</i> },
  { name: 'Prueba', icon: <i className='material-icons'>emoji_objects</i> },
  { name: 'Lead', icon: <i className='material-icons'>support_agent</i> }
]

const getContactIcon = formaContacto => {
  switch (formaContacto) {
    case 'llamada':
    case 'telefono':
      return 'call'
    case 'email':
    case 'e-mail':
      return 'forward_to_inbox'
    case 'persona':
    case 'tienda':
      return 'partner_exchange'
    case 'web':
      return 'screen_share'
    case 'whatsapp':
      return 'forum'
    default:
      return 'sync_alt'
  }
}

const getPruebaIcon = asistencia => {
  if (asistencia === 'VIENE') return 'check_circle'
  else if (asistencia === 'FALTA_JUSTIFICADA') return 'highlight_off'
  else if (asistencia === 'FALTA') return 'close'
  else return 'help_outline'
}

const showProximosPasos = estado => {
  ;[
    'PRUEBA',
    'CLIENTE_CAPTADO',
    'CLIENTE_PERDIDO',
    'LEAD_NO_VALIDO',
    'LEAD_DERIVADO'
  ].includes(estado)
}

const CampoContactado = ({ contacto }) => {
  if (['PRUEBA', 'CLIENTE_CAPTADO'].includes(contacto.estado)) return null
  if (contacto.contacto)
    return (
      <Typography
        className={`${css.detail} ${css.text}`}
        variant='body2'
        component='p'
      >
        <i className={`material-icons ${css.icon}`}>check_circle</i> Contactado
      </Typography>
    )
  return (
    <Typography
      className={`${css.detail} ${css.negativeText}`}
      variant='body2'
      component='p'
    >
      <i className={`material-icons ${css.negativeIcon}`}>close</i>No contactado
    </Typography>
  )
}

const LeadCard = ({ contacto, index, onClick }) => {
  if (contacto.tipo === 'llamada') {
    return (
      <LlamadaCard
        key={`llamada-${contacto.id}`}
        llamada={contacto}
        index={index}
        handleClick={() => onClick(contacto)}
      />
    )
  }
  if (contacto.tipo === 'contacto') {
    return (
      <ContactoCard
        key={`contacto-${contacto.id}`}
        index={index}
        contacto={contacto}
        onEdit={() => onClick(contacto)}
      />
    )
  }
  return null
}

const ContactoCard = ({ contacto, index, onEdit }) => (
  <Card className={css.card}>
    <CardContent>
      <div className={css.header}>
        <Typography variant='h6'>
          {contacto.estado.replace(/_/g, ' ')}
        </Typography>
        <div className={css.index}>{index + 1}</div>
      </div>
      <Typography className={css.detailGroup}>Detalles contacto</Typography>
      <Typography className={css.detail} variant='body2' component='p'>
        <i className='material-icons'>event</i> {contacto.fechaContacto}
      </Typography>
      <Typography className={css.detail} variant='body2' component='p'>
        <i className='material-icons'>face</i> {contacto.usuarioContacto}
      </Typography>
      <CampoContactado contacto={contacto} />
      {contacto.centroContacto && (
        <Typography className={css.detail} variant='body2' component='p'>
          <i className='material-icons'>school</i> {contacto.centroContacto}
        </Typography>
      )}
      {contacto.formaContacto && (
        <Typography className={css.detail} variant='body2' component='p'>
          <i className='material-icons'>
            {getContactIcon(contacto.formaContacto)}
          </i>{' '}
          {capitalize(contacto.formaContacto)}
        </Typography>
      )}
      {contacto.estado === 'PRUEBA' && (
        <>
          <Typography className={css.detailGroup}>
            Detalles prueba
            <Link
              className={css.detailEdit}
              component='button'
              variant='body2'
              onClick={onEdit}
            >
              Modificar detalles
            </Link>
          </Typography>
          <Typography className={css.detail} variant='body2' component='p'>
            <i className='material-icons'>event</i> {contacto.proximoContacto} →{' '}
            {contacto.hora}
          </Typography>
          <Typography className={css.detail} variant='body2' component='p'>
            <i className='material-icons'>school</i> {contacto.centroPrueba}
          </Typography>
          <Typography className={css.detail} variant='body2' component='p'>
            <i className='material-icons'>emoji_people</i>{' '}
            {contacto.profesor || 'Sin profesor'}
          </Typography>
          <Typography className={css.detail} variant='body2' component='p'>
            <i className='material-icons'>lightbulb_circle</i>{' '}
            {contacto.asignatura}
          </Typography>
          <Typography
            className={css.detail}
            variant='body2'
            component='p'
            data-confirmada={contacto.confirmada ? 'yes' : 'no'}
          >
            <i className='material-icons'>
              {contacto.confirmada ? 'check_circle' : 'psychology_alt'}
            </i>
            {contacto.confirmada
              ? 'Prueba confirmada'
              : 'Pendiente de confirmar'}
          </Typography>
          <Typography
            className={css.detail}
            variant='body2'
            component='p'
            data-asistencia={contacto.asistencia}
          >
            <i className='material-icons'>
              {getPruebaIcon(contacto.asistencia)}
            </i>
            {contacto.asistencia.replace(/_/g, ' ')}
          </Typography>
        </>
      )}
      {contacto.valoracion && (
        <>
          <Typography className={css.detailGroup}>Valoracion prueba</Typography>
          <blockquote className={css.blockInfo}>
            {contacto.valoracion}
          </blockquote>
        </>
      )}
      {contacto.presupuesto && (
        <>
          <Typography className={css.detailGroup}>
            Presupuesto ofrecido
          </Typography>
          <blockquote className={css.blockInfo}>
            {contacto.presupuesto}
          </blockquote>
        </>
      )}
      {contacto.alumnoId && (
        <>
          <Typography className={css.detailGroup}>Ficha de alumno</Typography>
          <Typography className={css.detail} variant='body2' component='p'>
            <i className='material-icons'>person_outline</i>
            <Link
              className={css.link}
              href={`/alumnos/${contacto.alumnoId}/ficha`}
            >
              {contacto.alumno}
            </Link>
          </Typography>
        </>
      )}
      {contacto.causa && (
        <>
          <Typography className={css.detailGroup}>Motivo de cierre</Typography>
          <Typography
            className={css.detail}
            variant='body2'
            component='p'
            data-confirmada='no'
          >
            <i className='material-icons'>close</i>
            {contacto.causa}
          </Typography>
        </>
      )}
      {contacto.observaciones && (
        <>
          <Typography className={css.detailGroup}>Observaciones</Typography>
          <blockquote className={css.blockInfo}>
            {contacto.observaciones}
          </blockquote>
        </>
      )}
      {showProximosPasos(contacto.estado) && (
        <>
          <Typography className={css.detailGroup}>Próximos pasos</Typography>
          <Typography className={css.detail} variant='body2' component='p'>
            <i className='material-icons'>event</i> {contacto.proximoContacto}
          </Typography>
          {contacto.fechaIncorporacion && (
            <Typography className={css.detail} variant='body2' component='p'>
              <i className='material-icons'>event</i>{' '}
              {contacto.fechaIncorporacion} (Fecha estimada incorporación)
            </Typography>
          )}
        </>
      )}
    </CardContent>
  </Card>
)

const LlamadaCard = ({ llamada, index, handleClick }) => {
  const {
    fechaInicio,
    duracionLlamada,
    estadoLlamada,
    tipoLlamada,
    telefono,
    usuario,
    resumen
  } = llamada
  const estadoRespuesta = getEstadoRespuestaLlamada(llamada)

  return (
    <Card className={`${css.card} ${css.llamadaCard}`}>
      <CardContent>
        <div className={css.header}>
          <Typography variant='h6'>
            <i className={`material-icons ${css.headerIcon}`}>call</i>
            Llamada -{' '}
            <span
              className={
                estadoRespuesta === 'RESPONDIDA' ? css.text : css.negativeText
              }
            >
              {estadoRespuesta}
            </span>
          </Typography>
          <div className={css.index}>{index + 1}</div>
        </div>
        <Typography className={css.detailGroup}>Detalles llamada</Typography>
        <Typography className={css.detail} variant='body2' component='p'>
          <i className='material-icons'>event</i>{' '}
          {dateToStringLong(fechaInicio) || '--'}
        </Typography>
        <Typography className={css.detail} variant='body2' component='p'>
          <i className='material-icons'>timer</i>{' '}
          {formatDuration(duracionLlamada)}
        </Typography>
        <Typography className={css.detail} variant='body2' component='p'>
          <i className='material-icons'>phone</i> {telefono || '--'}
        </Typography>
        <Typography className={css.detail} variant='body2' component='p'>
          <i className='material-icons'>person</i> {usuario?.nombre || '--'}
        </Typography>
        <Typography className={css.detail} variant='body2' component='p'>
          <i className='material-icons'>label</i>
          {tipoLlamada?.replace('LLAMADA_', '') || '--'} -{' '}
          {estadoLlamada || '--'}
        </Typography>

        <Button
          variant='outlined'
          size='small'
          startIcon={<i className='material-icons'>description</i>}
          onClick={handleClick}
          className={css.transcripcionButton}
        >
          Ver transcripción
        </Button>

        {resumen && (
          <>
            <Typography className={css.detailGroup}>Resumen</Typography>
            <blockquote className={css.blockInfo}>{resumen}</blockquote>
          </>
        )}
      </CardContent>
    </Card>
  )
}

const Contactos = () => {
  const userData = getAuth()
  const notification = useNotification()
  const queryClient = useQueryClient()

  const { isLoading, lead } = useLead()

  const { isLoading: isLoadingLlamadas, data: llamadasLead = [] } = useQuery({
    queryKey: ['llamada', 'list', 'feedback', lead?.id],
    queryFn: () => {
      if (!lead?.id) return []
      return getLlamadasLead(lead.id).catch(err => {
        notification.error({
          title: 'Error al recuperar las llamadas',
          content: err
        })
        return []
      })
    },
    enabled: !!lead?.id
  })

  const { contactos: leadContactos = [] } = lead

  const llamadasMapeadas = llamadasLead.map(llamada => ({
    ...llamada,
    tipo: 'llamada',
    fecha: dateToDateTime(llamada.fechaInicio)
  }))

  const contactosLead = leadContactos
    .map(contacto => ({
      ...contacto,
      tipo: 'contacto',
      fecha: getDateFromSpanishDateTime(contacto.fechaContacto)
    }))
    .concat(llamadasMapeadas)
    .sort((a, b) => compareRawDates(a.fecha, b.fecha))

  const [speedDialOpen, setSpeedDialOpen] = useState(false)
  const [nuevoContacto, setNuevoContacto] = useState(null)

  const [pruebaEdit, setPruebaEdit] = useState(null)
  const [llamadaSeleccionada, setLlamadaSeleccionada] = useState(null)

  const { isPending: isLeadUpdating, mutate: updateLead } = useMutation({
    mutationFn: addContacto,
    onSuccess: () => {
      notification.success({
        title: 'Lead actualizado',
        content: 'Contacto añadido correctamente'
      })
      queryClient.invalidateQueries({
        queryKey: ['marketing', 'feedback', 'list', 'CRM']
      })
      queryClient.invalidateQueries({
        queryKey: ['marketing', 'lead', lead.id]
      })
      setNuevoContacto(null)
      setSpeedDialOpen(false)
    },
    onError: err => {
      notification.error({ title: 'Error actualizando el lead', content: err })
    }
  })

  const { isPending: isPruebaUpdating, mutate: updatePrueba } = useMutation({
    mutationFn: modificarPrueba,
    onSuccess: () => {
      notification.success({
        title: 'Prueba actualizada',
        content: 'Prueba modificada correctamente'
      })
      queryClient.invalidateQueries({
        queryKey: ['marketing', 'feedback', 'list', 'CRM']
      })
      queryClient.invalidateQueries({
        queryKey: ['marketing', 'lead', lead.id]
      })
      setPruebaEdit(null)
    },
    onError: err => {
      notification.error({
        title: 'Error actualizando la prueba',
        content: err
      })
    }
  })

  const handleActionClick = action => {
    setNuevoContacto(action)
    setSpeedDialOpen(false)
  }

  const handleNuevoContacto = datos => {
    updateLead({
      id: lead.id,
      centroContacto: userData.centro,
      ...datos
    })
  }

  const handleClick = contacto => {
    if (contacto.tipo === 'llamada') {
      setLlamadaSeleccionada(contacto)
    }
    if (contacto.tipo === 'contacto') {
      setPruebaEdit(contacto)
    }
  }

  const handlePruebaEdit = datosPrueba => {
    if (isPruebaUpdating) return
    updatePrueba({ id: lead.id, ...datosPrueba })
  }

  return (
    <PageLoading isLoading={isLoading || isLoadingLlamadas}>
      <div className={css.main}>
        <LeadHeader label='Contactos' />
        <div className={css.content}>
          {contactosLead.map((item, index) => (
            <LeadCard
              key={index}
              contacto={item}
              index={index}
              onClick={handleClick}
            />
          ))}
        </div>
      </div>
      <SpeedDial
        className={css.speed}
        ariaLabel='Nuevo contacto'
        icon={<SpeedDialIcon />}
        onClose={() => setSpeedDialOpen(false)}
        onOpen={() => setSpeedDialOpen(true)}
        open={speedDialOpen}
      >
        {NUEVO_CONTACTO_OPTIONS.map(action => (
          <SpeedDialAction
            key={action.name}
            icon={action.icon}
            tooltipTitle={action.name}
            tooltipOpen
            onClick={() => handleActionClick(action.name)}
          />
        ))}
      </SpeedDial>
      <ModalContactoLead
        open={nuevoContacto === 'Lead'}
        disabled={isLeadUpdating}
        onClose={() => setNuevoContacto(null)}
        onSubmit={handleNuevoContacto}
      />
      <ModalContactoPrueba
        open={nuevoContacto === 'Prueba'}
        disabled={isLeadUpdating}
        onClose={() => setNuevoContacto(null)}
        onSubmit={handleNuevoContacto}
      />
      <ModalContactoLeadCualificado
        open={nuevoContacto === 'Presupuesto'}
        disabled={isLeadUpdating}
        onClose={() => setNuevoContacto(null)}
        onSubmit={handleNuevoContacto}
      />
      <ModalContactoLeadCerrado
        isSuccessEnabled
        open={nuevoContacto === 'Cerrar'}
        disabled={isLeadUpdating}
        onClose={() => setNuevoContacto(null)}
        onSubmit={handleNuevoContacto}
      />
      <ModalEditarPrueba
        open={!!pruebaEdit}
        pruebaAlumno={pruebaEdit}
        disabled={isPruebaUpdating}
        onClose={() => setPruebaEdit(null)}
        onSubmit={handlePruebaEdit}
      />
      <ModalTranscripcionLlamada
        open={!!llamadaSeleccionada}
        onClose={() => setLlamadaSeleccionada(null)}
        llamada={llamadaSeleccionada}
      />
    </PageLoading>
  )
}

export default Contactos
